// Раздел «Слова» листа управления песней (волна F, задача 7).
//
// Что это. Лист владельца (song-sheet.ts) отвечает на вопросы, которые
// задаются раз в месяц: кто эту песню видит, кто её открывал, как снять её с
// публикации. Этот модуль отвечает на них про СЛОВА. Форма взята у прототипа
// (docs/prototypes/2026-09-18-online-library.html, `buildOwnerPanel`): секция
// `.op-sec` с подписью-надзаголовком `.op-head`, под ней орган, а под органом
// — поясняющая строка `.op-explain`, потому что «Team или Public» — ровно то
// единственное здесь, что можно испортить так, что никто не заметит.
//
// Модуль ЧИСТЫЙ, как и song-sheet.ts рядом: ни localStorage, ни `t()`, ни
// сети. Подписи, готовый адрес ссылки, готовая строка «открывали когда-то» и
// обработчики приезжают параметрами; перевод, форматирование дат и сетевые
// клиенты остаются у library-view.ts. Причина не стилистическая: чистый модуль
// проверяется своими подписями-маркерами, то есть тест ловит ПОВЕДЕНИЕ, а не
// текущее содержимое словаря.
//
// ── Четыре вещи, которые обязаны быть именно так ───────────────────────────
//
// 1. ИМЁН ЧИТАТЕЛЕЙ ЗДЕСЬ НЕ БУДЕТ. Решение схемы 0018 держится дословно: «кто
//    открыл не пишется и не показывается — для команды из четырёх человек это
//    слежка». Раздел показывает только ВРЕМЯ последнего открытия, и показать
//    больше ему нечем: сервер имён и не отдаёт. Замок — тест «в разделе слов
//    нет ни одного имени читателя».
//
// 2. СМЕНА НА «ТОЛЬКО КОМАНДА» ГАСИТ УЖЕ РАЗОСЛАННУЮ ССЫЛКУ. Это осознанное
//    следствие («убрал значит убрал»), и человек обязан узнать о нём ДО
//    нажатия, а не после — поэтому предупреждение стоит в пояснении, которое
//    видно, пока выбрано «любой по ссылке», то есть ровно у того, кому есть
//    что гасить. То же предупреждение стоит в тексте подтверждения снятия
//    слов, где последствие то же самое и необратимее.
//
// 3. ОТКАЗ API НЕ ВРЁТ ПРО СОСТОЯНИЕ. Переключатель возвращается в прежнее
//    положение, причина называется словом. Оптимистичный интерфейс, рисующий
//    несделанное как сделанное, в этом проекте уже ловили — поэтому раздел
//    перерисовывается только ПОСЛЕ успешного ответа, и `current` меняется там
//    же, одной строкой.
//
// 4. ВИДИМОСТЬ ЗНАЕТ ТОЛЬКО ВЛАДЕЛЕЦ СЛОВ. Гейт на это стоит у вызывающего
//    (library-view.ts): чужих слов раздел не получает вовсе. Здесь его нет
//    намеренно — модуль не знает ни почты смотрящего, ни чьи это слова, и
//    второе место, решающее тот же вопрос, разошлось бы с первым молча.

// Сегментированный орган DS — тот же, что у вкладок библиотеки и у видимости
// аудио-версии. Импорт регистрирует кастомный элемент, его light-DOM CSS едет
// рядом (без второго импорта орган рисуется голым).
import '@bandway/ui-components';
import '@bandway/ui-components/styles/bw-segmented-control.css';
import type { SongTextWriteResult } from '../song-text-api.js';

export interface SongSheetWordsLabels {
  /** Надзаголовок секции — «Who can see the words». */
  heading: string;
  /** Подписи двух положений переключателя. */
  team: string;
  public: string;
  /** Что ЗНАЧИТ выбранное положение — строка под органом (`op-explain`).
   *  `explainPublic` несёт и предупреждение про гашение ссылки (пункт 2). */
  explainTeam: string;
  explainPublic: string;
  /** Подпись поля с адресом и кнопки копирования. */
  linkLabel: string;
  copy: string;
  /** Подтверждение копирования — заменяет подпись кнопки на пару секунд. */
  copied: string;
  /** Скопировать НЕ получилось. Отдельная строка, а не молчание: буфер обмена
   *  ничем себя не показывает, и человек, увидевший «Скопировано» там, где
   *  ничего не скопировалось, узнает об этом, только вставив пустоту. */
  copyFailed: string;
  /** «Ещё никто не открывал» — когда времени нет. */
  neverOpened: string;
  /** Снятие слов с публикации и его подтверждение. */
  removeBtn: string;
  /** Текст подтверждения. ОБЯЗАН называть последствие: ссылка перестанет
   *  открываться (пункт 2 в шапке). */
  removeWarning: string;
  removeConfirm: string;
  removeCancel: string;
  /** Причины отказа — по ступеням лестницы маршрута, каждая своим словом.
   *  «Не удалось» одним словом на все четыре не говорит человеку ничего:
   *  «войдите заново», «это не ваши слова», «их уже нет» и «сервер не ответил»
   *  требуют разных действий. */
  errAuth: string;
  errForbidden: string;
  errGone: string;
  errNetwork: string;
}

export interface SongSheetWordsOpts {
  /** Текущее состояние — ОТРАЖАЕТСЯ, а не угадывается. */
  visibility: 'team' | 'public';
  /** Готовый адрес публичной ссылки. Собирает вызывающий: `location` —
   *  окружение, а не данные раздела. */
  linkUrl: string;
  /** Готовая строка «открывали тогда-то», или null — ни разу. Форматирование
   *  дат (относительная подсказка, локаль) остаётся у вызывающего. */
  openedLabel: string | null;
  labels: SongSheetWordsLabels;
  /** PATCH видимости. */
  setVisibility: (next: 'team' | 'public') => Promise<SongTextWriteResult>;
  /** DELETE — снятие слов с публикации. */
  removeWords: () => Promise<SongTextWriteResult>;
  /**
   * Положить адрес в буфер обмена и СКАЗАТЬ, получилось ли. Инъекция, а не
   * `navigator.clipboard` внутри: буфер — это разрешение браузера и side
   * effect, а модуль чистый.
   *
   * Возврат обязателен по существу. `navigator.clipboard` отсутствует на
   * http-странице и бросает в приватном окне; без ответа раздел писал бы
   * «Скопировано» и там, где ничего не скопировалось, — то есть врал бы про
   * сделанное в единственном месте, где проверить нечем: буфер невидим.
   */
  copyLink?: (url: string) => boolean | Promise<boolean>;
  /** Слова сняты — вызывающему пора перерисовать библиотеку и закрыть лист. */
  onRemoved?: () => void;
}

/** Сколько подпись кнопки держится на «Скопировано» — столько же, сколько в
 *  share-modal.ts, чтобы одно и то же действие вело себя одинаково. */
const COPIED_MS = 2000;

/** Сегментированный орган DS: значение читается и пишется свойством. */
type SegmentedEl = HTMLElement & {
  segments: Array<{ value: string; label: string }>;
  value: string;
};

export function buildSongSheetWords(opts: SongSheetWordsOpts): HTMLElement {
  const { labels: L } = opts;

  const sec = document.createElement('section');
  sec.dataset.role = 'sheet-words';
  sec.className = 'lib-sheet-sec';

  const head = document.createElement('h3');
  head.dataset.role = 'sheet-words-head';
  head.className = 'lib-sheet-sec-head';
  head.textContent = L.heading;
  sec.appendChild(head);

  // Тело секции перерисовывается целиком при смене видимости: адрес ссылки
  // появляется и исчезает, пояснение меняется. Надзаголовок остаётся снаружи —
  // он не зависит ни от чего.
  const bodyEl = document.createElement('div');
  bodyEl.className = 'lib-sheet-sec-body';
  sec.appendChild(bodyEl);

  // Строка состояния — ОДНА на весь раздел и ЗА пределами перерисовываемого
  // тела: причина отказа обязана пережить перерисовку, иначе человек увидит
  // возврат переключателя без объяснения, что и есть «врать про состояние».
  const status = document.createElement('p');
  status.dataset.role = 'words-vis-status';
  status.className = 'lib-sheet-sec-status';
  status.setAttribute('aria-live', 'polite');
  sec.appendChild(status);

  /** Известное состояние. Меняется ТОЛЬКО после успешного ответа (пункт 3). */
  let current: 'team' | 'public' = opts.visibility;
  /** Подтверждение снятия открыто. Живёт снаружи paint(), чтобы перерисовка
   *  видимости его не роняла. */
  let confirming = false;

  const reasonText = (reason: 'auth' | 'forbidden' | 'gone' | 'network'): string => {
    switch (reason) {
      case 'auth': return L.errAuth;
      case 'forbidden': return L.errForbidden;
      case 'gone': return L.errGone;
      default: return L.errNetwork;
    }
  };

  /**
   * Перерисовать И ПОДОБРАТЬ ФОКУС. `paint()` делает `innerHTML = ''`, то есть
   * уничтожает узел, с которого пришло нажатие, — и фокус падает на <body>.
   * Мышью этого не видно; с клавиатуры человек, дошедший табом до «Убрать» и
   * нажавший Enter, получает отрисованное подтверждение, до которого не
   * дотянуться иначе как новым проходом Tab с начала документа.
   *
   * Цель называет вызывающий, потому что безопасный выбор у каждого перехода
   * свой: открыли подтверждение — «Отмена» (случайный Enter ничего не снимет),
   * закрыли — обратно на «Убрать», откуда пришли.
   */
  function paintFocusing(role: string): void {
    paint();
    bodyEl.querySelector<HTMLElement>(`[data-role="${role}"]`)?.focus();
  }

  function paint(): void {
    bodyEl.innerHTML = '';

    // ── Переключатель ──
    const seg = document.createElement('bw-segmented-control') as SegmentedEl;
    seg.dataset.role = 'words-vis-seg';
    seg.setAttribute('size', 'sm');
    seg.setAttribute('aria-label', L.heading);
    seg.segments = [
      { value: 'team', label: L.team },
      { value: 'public', label: L.public },
    ];
    seg.value = current;
    bodyEl.appendChild(seg);

    // ── Пояснение под органом, а не в подсказке ──
    const explain = document.createElement('p');
    explain.dataset.role = 'words-vis-explain';
    explain.className = 'lib-sheet-sec-explain';
    explain.textContent = current === 'public' ? L.explainPublic : L.explainTeam;
    bodyEl.appendChild(explain);

    seg.addEventListener('bw-segmented-change', (e) => {
      const next = (e as CustomEvent<{ value: string }>).detail?.value === 'public' ? 'public' : 'team';
      if (next === current) return;
      status.textContent = '';
      Promise.resolve(opts.setVisibility(next)).then(
        (res) => {
          if (res.ok) { current = next; paint(); return; }
          // Возврат В ПРЕЖНЕЕ положение и причина словом (пункт 3). Раздел НЕ
          // перерисовывается: рисовать публичное состояние после отказа —
          // ровно то враньё, против которого этот путь и написан.
          seg.value = current;
          status.textContent = reasonText(res.reason);
        },
        () => { seg.value = current; status.textContent = L.errNetwork; },
      );
    });

    // ── Адрес ссылки — ТОЛЬКО при public ──
    // При «только команда» ссылки, которую можно кому-то дать, не существует:
    // показать адрес значило бы предложить отправить то, что не откроется.
    if (current === 'public') bodyEl.appendChild(buildLinkRow());

    // ── Когда открывали. Только время — имён здесь не будет (пункт 1) ──
    const opened = document.createElement('p');
    opened.dataset.role = 'words-opened';
    opened.className = 'lib-sheet-sec-meta';
    opened.textContent = opts.openedLabel ?? L.neverOpened;
    bodyEl.appendChild(opened);

    // ── Снятие с публикации ──
    bodyEl.appendChild(confirming ? buildConfirm() : buildRemoveBtn());
  }

  function buildLinkRow(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.dataset.role = 'words-link';
    wrap.className = 'lib-sheet-link';

    const url = document.createElement('input');
    url.dataset.role = 'words-link-url';
    url.className = 'lib-sheet-link-url';
    url.type = 'text';
    url.readOnly = true;
    url.value = opts.linkUrl;
    url.setAttribute('aria-label', L.linkLabel);
    wrap.appendChild(url);

    const copy = document.createElement('button');
    copy.type = 'button';
    copy.dataset.role = 'words-link-copy';
    copy.className = 'lib-sheet-link-copy';
    copy.textContent = L.copy;
    copy.addEventListener('click', () => {
      status.textContent = '';
      // Подтверждение действия на самой кнопке — но ТОЛЬКО по факту: без
      // ответа непонятно, случилось ли что-нибудь (буфер обмена ничем себя не
      // показывает), а с нарисованным заранее «Скопировано» — понятно
      // неверно. Отказ называется словом и оставляет подпись кнопки прежней:
      // адрес рядом выделяемый, скопировать его руками можно.
      Promise.resolve(opts.copyLink?.(opts.linkUrl) ?? false).then(
        (ok) => {
          if (!ok) { status.textContent = L.copyFailed; return; }
          copy.textContent = L.copied;
          setTimeout(() => { copy.textContent = L.copy; }, COPIED_MS);
        },
        () => { status.textContent = L.copyFailed; },
      );
    });
    wrap.appendChild(copy);

    return wrap;
  }

  function buildRemoveBtn(): HTMLElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.role = 'words-remove';
    btn.className = 'lib-sheet-remove';
    btn.textContent = L.removeBtn;
    btn.addEventListener('click', () => { confirming = true; paintFocusing('words-remove-no'); });
    return btn;
  }

  /**
   * Подтверждение на месте кнопки, а не модалкой поверх листа: лист сам —
   * модальная поверхность, и вторая поверх неё забирает фокус у первой.
   * Текст ОБЯЗАН называть последствие (пункт 2): слова уйдут из библиотеки, и
   * уже разосланная ссылка перестанет открываться — у всех.
   */
  function buildConfirm(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.dataset.role = 'words-remove-confirm';
    wrap.className = 'lib-sheet-confirm';

    const text = document.createElement('p');
    text.className = 'lib-sheet-confirm-text';
    text.textContent = L.removeWarning;
    wrap.appendChild(text);

    const row = document.createElement('div');
    row.className = 'lib-sheet-confirm-row';

    const no = document.createElement('button');
    no.type = 'button';
    no.dataset.role = 'words-remove-no';
    no.className = 'lib-sheet-confirm-no';
    no.textContent = L.removeCancel;
    no.addEventListener('click', () => { confirming = false; paintFocusing('words-remove'); });
    row.appendChild(no);

    const yes = document.createElement('button');
    yes.type = 'button';
    yes.dataset.role = 'words-remove-yes';
    yes.className = 'lib-sheet-confirm-yes';
    yes.textContent = L.removeConfirm;
    yes.addEventListener('click', () => {
      yes.disabled = true;
      status.textContent = '';
      Promise.resolve(opts.removeWords()).then(
        (res) => {
          if (res.ok) { opts.onRemoved?.(); return; }
          // Не получилось — раздел остаётся на месте, действие снова доступно,
          // причина названа. Тихо закрыть подтверждение значило бы показать
          // снятие, которого не было.
          confirming = false;
          paint();
          status.textContent = reasonText(res.reason);
        },
        () => { confirming = false; paint(); status.textContent = L.errNetwork; },
      );
    });
    row.appendChild(yes);

    wrap.appendChild(row);
    return wrap;
  }

  paint();
  return sec;
}
